import { FC, useCallback, useContext, useEffect, useState } from "react";

import {
  ArrowBack,
  Group,
  History,
  MeetingRoom,
  Videocam,
  VideoCall,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ConnectMeetingModel,
  ConnectMeetingSummary,
  ConnectOccurrenceModel,
  getConnectOccurrence,
  postConnectMeeting,
  postConnectMeetingEnd,
} from "@rubi/api-generated/clientRs";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";

import ConfirmDialog from "./ConfirmDialog";
import InvitationsDialog from "./InvitationsDialog";
import LifecycleChip from "./LifecycleChip";
import MeetingHistoryDialog from "./MeetingHistoryDialog";
import OfficerRoomDialog from "./OfficerRoomDialog";
import WaitingRoomDialog from "./WaitingRoomDialog";

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const formatScheduled = (m: ConnectMeetingModel) => {
  if (!m.scheduled_at) return null;
  const opts: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
  };
  if (m.timezone) opts.timeZone = m.timezone;
  try {
    return `${new Date(m.scheduled_at).toLocaleString(undefined, opts)}${
      m.timezone ? ` (${m.timezone})` : ""
    }`;
  } catch {
    return new Date(m.scheduled_at).toLocaleString();
  }
};

// A closed meeting can't be joined and has no live waiting room — the backend
// rejects both, so the portal disables those actions too.
const isMeetingClosed = (m: ConnectMeetingSummary) =>
  m.lifecycle.phase === "ended" ||
  m.lifecycle.phase === "archived" ||
  m.status === "ended" ||
  m.status === "completed";

const OccurrenceView: FC = () => {
  const { t } = useTranslation("connect");
  const navigate = useNavigate();
  const { occurrenceId } = useParams<{ orgId: string; occurrenceId: string }>();
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);
  const orgId = getOrgIDFromURL() ?? currentUser?.rubi_org_id ?? "";
  const canCreateMeeting = userPermissionChecks(
    currentUser?.permissions?.map((p) => p.title) ?? [],
  ).canCreateConnectMeeting;

  const [occurrence, setOccurrence] = useState<ConnectOccurrenceModel | null>(
    null,
  );
  const [meetings, setMeetings] = useState<ConnectMeetingSummary[]>([]);
  const [loading, setLoading] = useState(true);

  // Create-meeting dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [timezone, setTimezone] = useState(browserTimezone());
  const [creating, setCreating] = useState(false);

  // Per-meeting dialogs
  const [invitesFor, setInvitesFor] = useState<ConnectMeetingModel | null>(
    null,
  );
  const [waitingFor, setWaitingFor] = useState<ConnectMeetingModel | null>(
    null,
  );
  const [roomFor, setRoomFor] = useState<ConnectMeetingModel | null>(null);
  const [endTarget, setEndTarget] = useState<ConnectMeetingModel | null>(null);
  const [historyFor, setHistoryFor] = useState<ConnectMeetingModel | null>(
    null,
  );

  const load = useCallback(async () => {
    if (!orgId || !occurrenceId) return;
    setLoading(true);
    try {
      const res = await getConnectOccurrence({
        path: { org_id: orgId, occurrence_id: occurrenceId },
      });
      setOccurrence(res.data?.occurrence ?? null);
      setMeetings(res.data?.meetings ?? []);
    } catch {
      toast.error(t("occurrence.toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [orgId, occurrenceId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
    setTitle("");
    setScheduledAt("");
    setTimezone(browserTimezone());
  };

  const handleCreateMeeting = async () => {
    if (!title.trim()) {
      toast.error(t("occurrence.toast.titleRequired"));
      return;
    }
    setCreating(true);
    try {
      // datetime-local is wall-clock in the officer's browser; send the UTC
      // instant and keep the chosen zone for display.
      const scheduledIso = scheduledAt
        ? new Date(scheduledAt).toISOString()
        : undefined;
      await postConnectMeeting({
        path: { org_id: orgId },
        body: {
          title: title.trim(),
          occurrence_id: occurrenceId,
          scheduled_at: scheduledIso,
          timezone: timezone.trim() || undefined,
        },
      });
      toast.success(t("occurrence.toast.meetingCreated"));
      closeCreate();
      await load();
    } catch {
      toast.error(t("occurrence.toast.createFailed"));
    } finally {
      setCreating(false);
    }
  };

  const endMeeting = async () => {
    const m = endTarget;
    if (!m) return;
    setEndTarget(null);
    try {
      await postConnectMeetingEnd({
        path: { org_id: orgId, meeting_id: m.id },
      });
      toast.success(t("occurrence.toast.sessionEnded"));
      await load();
    } catch {
      toast.error(t("occurrence.toast.endFailed"));
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
      <Link
        component="button"
        underline="hover"
        onClick={() => navigate(`/rubiconnect/${orgId}/occurrences`)}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 2 }}
      >
        <ArrowBack fontSize="small" /> {t("occurrence.back")}
      </Link>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !occurrence ? (
        <Typography color="text.secondary">
          {t("occurrence.notFound")}
        </Typography>
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{ mb: 3, gap: 2, flexWrap: "wrap" }}
          >
            <Box>
              <Typography variant="h5">
                {occurrence.occurrence_number}
              </Typography>
              {occurrence.reference_tag && (
                <Typography variant="body2" color="text.secondary">
                  {occurrence.reference_tag}
                </Typography>
              )}
            </Box>
            {canCreateMeeting && (
              <Button
                variant="contained"
                startIcon={<VideoCall />}
                onClick={() => setCreateOpen(true)}
              >
                {t("occurrence.createMeeting")}
              </Button>
            )}
          </Stack>

          <Typography variant="subtitle1" gutterBottom>
            {t("occurrence.meetings.heading")}
          </Typography>
          {meetings.length === 0 ? (
            <Typography color="text.secondary">
              {t("occurrence.meetings.empty")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {meetings.map((m) => (
                <Card key={m.id} variant="outlined">
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography variant="subtitle2">
                        {m.title}
                        {m.meeting_number ? ` · ${m.meeting_number}` : ""}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatScheduled(m) ??
                          t("occurrence.meetings.notScheduled")}
                      </Typography>
                    </Box>
                    <LifecycleChip lifecycle={m.lifecycle} />
                    {canCreateMeeting && (
                      <Button
                        size="small"
                        startIcon={<Group />}
                        onClick={() => setInvitesFor(m)}
                      >
                        {t("occurrence.meetings.invites")}
                      </Button>
                    )}
                    <Button
                      size="small"
                      startIcon={<MeetingRoom />}
                      onClick={() => setWaitingFor(m)}
                      disabled={isMeetingClosed(m)}
                    >
                      {t("occurrence.meetings.waitingRoom")}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Videocam />}
                      onClick={() => setRoomFor(m)}
                      disabled={isMeetingClosed(m)}
                    >
                      {t("occurrence.meetings.join")}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<History />}
                      onClick={() => setHistoryFor(m)}
                    >
                      {t("occurrence.meetings.history")}
                    </Button>
                    {!isMeetingClosed(m) && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setEndTarget(m)}
                      >
                        {t("occurrence.meetings.end")}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </>
      )}

      <Dialog open={createOpen} onClose={closeCreate} maxWidth="sm" fullWidth>
        <DialogTitle>{t("occurrence.createDialog.title")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t("occurrence.createDialog.titleLabel")}
              required
              fullWidth
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              label={t("occurrence.createDialog.scheduledLabel")}
              type="datetime-local"
              fullWidth
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t("occurrence.createDialog.timezoneLabel")}
              fullWidth
              value={timezone}
              InputProps={{ readOnly: true }}
              helperText={t("occurrence.createDialog.timezoneHelp")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate} disabled={creating}>
            {t("occurrence.createDialog.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateMeeting}
            disabled={creating || !title.trim()}
          >
            {creating
              ? t("occurrence.createDialog.creating")
              : t("occurrence.createDialog.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mount each dialog only while open so every open is a fresh mount —
          this isolates per-meeting state (no stale participant list from a
          previously-opened meeting) and re-anchors the officer's session clock
          to when they open the room rather than to page load. */}
      {invitesFor && (
        <InvitationsDialog
          open
          onClose={() => setInvitesFor(null)}
          orgId={orgId}
          meetingId={invitesFor.id}
          meetingTitle={invitesFor.title}
        />
      )}
      {waitingFor && (
        <WaitingRoomDialog
          open
          onClose={() => setWaitingFor(null)}
          orgId={orgId}
          meetingId={waitingFor.id}
          meetingTitle={waitingFor.title}
        />
      )}
      {roomFor && (
        <OfficerRoomDialog
          open
          onClose={() => setRoomFor(null)}
          orgId={orgId}
          meeting={roomFor}
        />
      )}
      {historyFor && (
        <MeetingHistoryDialog
          open
          onClose={() => setHistoryFor(null)}
          orgId={orgId}
          meeting={historyFor}
        />
      )}

      <ConfirmDialog
        open={endTarget !== null}
        title={t("occurrence.endDialog.title")}
        message={t("occurrence.endDialog.message", {
          title: endTarget?.title ?? "",
        })}
        confirmLabel={t("occurrence.endDialog.confirm")}
        destructive
        onConfirm={endMeeting}
        onCancel={() => setEndTarget(null)}
      />
    </Box>
  );
};

export default OccurrenceView;
